import React from 'react';
import { Loader2 } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { NotificationBlock, insertNotificationToken } from '../PrestationPlanning/PrestationNotificationsPage';
import { ModeNotificationsContext, FournisseurEnregistrement, EtatEnregistrement, GroupeNotifications } from '../ui/LigneNotification';

// Toutes les variables des 9 emails superadmin réunies, disponibles dans chaque bloc (même principe que
// côté client : le serveur calcule toujours l'intégralité du jeu de variables, celles qui ne concernent
// pas l'email en cours restent simplement vides).
const ALL_ADMIN_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom établissement' },
    { token: '{{client_nom}}', label: 'Nom / contact client' },
    { token: '{{identifiant}}', label: 'Identifiant' },
    { token: '{{mot_de_passe}}', label: 'Mot de passe' },
    { token: '{{collaborateur_nom}}', label: 'Nom du collaborateur' },
    { token: '{{salarie_prenom}}', label: 'Prénom du salarié' },
    { token: '{{salarie_nom}}', label: 'Nom du salarié' },
    { token: '{{motif}}', label: 'Motif' },
    { token: '{{message}}', label: 'Message' },
    { token: '{{lien}}', label: 'Lien du message' },
    // Le bouton se pose depuis le modèle : c'est celui qui rédige qui décide où il tombe.
    { token: '{{bouton_reinitialisation}}', label: 'Bouton « Choisir mon mot de passe »' },
    { token: '{{type_client}}', label: 'Type de client' },
];

const SENT_EMAIL_TYPES: { key: string; title: string; description: string }[] = [
    { key: 'client_create', title: 'Création de compte client', description: "Envoyé à la création d'un nouveau client (bouton \"Ajouter un client\")." },
    { key: 'client_reset_password', title: 'Réinitialisation mot de passe client', description: "Envoyé quand le client (ou vous, à sa demande) réinitialise son propre mot de passe — bouton \"Réinitialiser\"." },
    { key: 'client_force_reset_password', title: 'Réinitialisation du mot de passe client', description: "Envoyé quand vous forcez la réinitialisation — bouton \"Forcer la réinitialisation\". AUCUN MOT DE PASSE N'EST ENVOYÉ : {{lien}} contient le lien vers la page où le client choisit le sien, valable 24 h et utilisable une seule fois. Placez {{bouton_reinitialisation}} où vous voulez voir le bouton. Si vous n'insérez ni le bouton ni {{lien}}, le bouton « Choisir mon mot de passe » est ajouté à la fin — le message ne part jamais sans son lien." },
    { key: 'collaborator_credentials', title: 'Identifiants collaborateur', description: "Envoyé à la création d'un collaborateur, à l'acceptation d'une demande de collaborateur, ou au reset normal de son mot de passe." },
    { key: 'collaborator_force_reset_password', title: 'Mot de passe collaborateur réinitialisé de force', description: "Envoyé quand vous forcez la réinitialisation du mot de passe d'un collaborateur — bouton \"Forcer la réinitialisation\", ton sécurité distinct du mail d'identifiants normal." },
    { key: 'collaborator_request_refuse', title: 'Refus de demande de collaborateur', description: "Envoyé au client lorsqu'une demande de création de collaborateur est refusée." },
    { key: 'employee_reset_password', title: 'Réinitialisation mot de passe salarié', description: "Envoyé lors du reset du mot de passe portail d'un salarié." },
    { key: 'support_reply', title: 'Réponse à un ticket support', description: 'Envoyé au client lorsque vous répondez à un message dans le support admin.' },
    { key: 'registration_invite', title: "Invitation à s'inscrire", description: "Envoyé lors de l'envoi d'un formulaire d'inscription à un futur client. {{lien}} contient l'adresse du formulaire." },
    { key: 'registration_accept', title: "Acceptation de la demande d'inscription", description: "Envoyé lorsque vous acceptez une demande d'inscription (identifiants de connexion)." },
    { key: 'registration_refuse', title: "Refus de la demande d'inscription", description: "Envoyé lorsque vous refusez une demande d'inscription." },
];

const RECEIVED_EMAIL_TYPES: { key: string; title: string; description: string }[] = [
    { key: 'registration_submitted', title: "Nouvelle demande d'inscription soumise", description: "Reçu lorsqu'un prospect a rempli et envoyé son formulaire d'inscription." },
    { key: 'support_new_message', title: 'Nouveau message support', description: 'Reçu lorsqu\'un client envoie un message dans le support.' },
    { key: 'signature_digitize_request', title: 'Demande de numérisation de signature', description: "Reçu lorsqu'un client ou un salarié soumet une photo de signature à numériser." },
    { key: 'collaborator_request_new', title: 'Nouvelle demande de collaborateur', description: 'Reçu lorsqu\'un client soumet une demande de création de compte collaborateur.' },
    { key: 'exception_service_automation_request', title: "Demande d'automatisation Service d'exception", description: "Reçu lorsqu'un client demande la connexion d'une plateforme de réservation (Zenchef...) pour automatiser la saisie des réservations." },
];

/**
 * À QUEL MODULE APPARTIENT CHAQUE EMAIL.
 *
 * Un réglage se range là où on s'en sert : les courriels des comptes clients vivent dans Gestion
 * Clients, ceux du support dans le Support, celui des automatisations dans son écran. Il n'y a plus
 * d'onglet « Notifications » dans les Paramètres — on n'allait l'y chercher qu'en sachant déjà qu'il
 * existait.
 *
 * Les seize clés sont réparties, sans reste : c'est vérifié par GROUPES_COMPLETS ci-dessous.
 */
export type GroupeEmails = 'clients' | 'support' | 'automatisation';

const GROUPE_DE: Record<string, GroupeEmails> = {
    client_create: 'clients',
    client_reset_password: 'clients',
    client_force_reset_password: 'clients',
    collaborator_credentials: 'clients',
    collaborator_force_reset_password: 'clients',
    collaborator_request_refuse: 'clients',
    collaborator_request_new: 'clients',
    employee_reset_password: 'clients',
    registration_invite: 'clients',
    registration_accept: 'clients',
    registration_refuse: 'clients',
    registration_submitted: 'clients',
    signature_digitize_request: 'clients',
    support_reply: 'support',
    support_new_message: 'support',
    exception_service_automation_request: 'automatisation',
};

/** Aucun email ne doit rester orphelin : sans cela, un réglage deviendrait inatteignable. */
export const emailsSansGroupe = (): string[] =>
    [...SENT_EMAIL_TYPES, ...RECEIVED_EMAIL_TYPES].map((t) => t.key).filter((k) => !GROUPE_DE[k]);

/** Un bloc autonome (garde ses propres refs subject/body, stables pour toute sa durée de vie) — évite
 *  le piège d'une map de refs dynamique qui ne s'attacherait jamais réellement aux éléments DOM. */
const AdminEmailTypeBlock: React.FC<{
    title: string;
    description: string;
    subject: string;
    body: string;
    onChangeSubject: (v: string) => void;
    onChangeBody: (v: string) => void;
}> = ({ title, description, subject, body, onChangeSubject, onChangeBody }) => {
    const [activeField, setActiveField] = React.useState<'subject' | 'body'>('body');
    const subjectRef = React.useRef<HTMLInputElement>(null);
    const bodyRef = React.useRef<HTMLTextAreaElement>(null);

    const insertToken = (token: string) => {
        if (activeField === 'subject') insertNotificationToken(subjectRef.current, subject, onChangeSubject, token);
        else insertNotificationToken(bodyRef.current, body, onChangeBody, token);
    };

    return (
        <NotificationBlock
                title={title}
                description={description}
                subject={subject} setSubject={onChangeSubject}
                body={body} setBody={onChangeBody}
                subjectRef={subjectRef} bodyRef={bodyRef}
                activeField={activeField} setActiveField={setActiveField}
                onInsertToken={insertToken}
                tokens={ALL_ADMIN_TOKENS}
            />
    );
};

export const AdminNotificationSettings: React.FC<{ groupe?: GroupeEmails }> = ({ groupe }) => {
    const [values, setValues] = React.useState<Record<string, string>>({});
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [echec, setEchec] = React.useState(false);

    React.useEffect(() => {
        adminApi.getAdminNotificationSettings().then((s: any) => setValues(s || {})).catch((e) => console.error('Failed to load admin notification settings', e)).finally(() => setLoading(false));
    }, []);

    const setField = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }));

    const handleSave = async () => {
        setSaving(true);
        setEchec(false);
        try {
            const result: any = await adminApi.saveAdminNotificationSettings(values);
            if (result) setValues(result);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save admin notification settings', e);
            setEchec(true);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-content-muted" size={28} /></div>;
    }

    return (
        <ModeNotificationsContext.Provider value={{ enLignes: true }}>
            <FournisseurEnregistrement enregistrer={handleSave}>
                <div className="space-y-6 max-w-3xl">
            {SENT_EMAIL_TYPES.some((t) => !groupe || GROUPE_DE[t.key] === groupe) && (
                <GroupeNotifications titre="Emails que vous envoyez">
                    {SENT_EMAIL_TYPES.filter((t) => !groupe || GROUPE_DE[t.key] === groupe).map((t) => (
                        <AdminEmailTypeBlock
                            key={t.key}
                            title={t.title}
                            description={t.description}
                            subject={values[t.key + '_subject'] || ''}
                            body={values[t.key + '_body'] || ''}
                            onChangeSubject={(v) => setField(t.key + '_subject', v)}
                            onChangeBody={(v) => setField(t.key + '_body', v)}
                        />
                    ))}
                </GroupeNotifications>
            )}
            {RECEIVED_EMAIL_TYPES.some((t) => !groupe || GROUPE_DE[t.key] === groupe) && (
                <GroupeNotifications titre="Emails que vous recevez">
                    {RECEIVED_EMAIL_TYPES.filter((t) => !groupe || GROUPE_DE[t.key] === groupe).map((t) => (
                        <AdminEmailTypeBlock
                            key={t.key}
                            title={t.title}
                            description={t.description}
                            subject={values[t.key + '_subject'] || ''}
                            body={values[t.key + '_body'] || ''}
                            onChangeSubject={(v) => setField(t.key + '_subject', v)}
                            onChangeBody={(v) => setField(t.key + '_body', v)}
                        />
                    ))}
                </GroupeNotifications>
            )}
                </div>
                <EtatEnregistrement etat={saving ? 'en_cours' : echec ? 'echec' : saved ? 'fait' : 'repos'} />
            </FournisseurEnregistrement>
        </ModeNotificationsContext.Provider>
    );
};
